import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { solutions } from '../../data/solutionCatalog';
import { demoCenterPath } from '../../data/demoCenter';
import { DomainIcon } from '../demoCenter/DomainIcon';
import { PageHero } from '../layout/PageHero';
import { easeOutStrong } from '../../utils/motion';

/** Solutions overview: hero and seven solution cards. */
export function SolutionsIntro() {
  return <>
      <PageHero eyebrow="Solutions" title="One connected system. Configured for your work." lead="Choose a solution built around the people you serve and the work you need to coordinate. Noetivra combines shared modules with dedicated workspaces for each setting." data-id="element-2696" data-mp="1k98d1xsttr90">
        <a href="/how-it-works" className="mt-6 inline-flex items-center gap-2 font-semibold text-navy hover:text-navy-light transition-colors duration-150 whitespace-nowrap" data-id="element-2697" data-mp="1g7msrrdyqaos">
          See how it works <ArrowRight size={16} aria-hidden="true" data-id="element-2698" data-mp="3ac6b3v9www98" />
        </a>
      </PageHero>

      <section aria-labelledby="solution-list-title" className="bg-surface-bg border-b border-line py-16 lg:py-24" data-id="element-2699" data-mp="3a9tnb1hh3ro9">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2700" data-mp="3k1zmav6579cy">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10" data-id="element-2701" data-mp="3k17qpkcjpbmo">
            <h2 id="solution-list-title" className="font-heading font-bold text-3xl text-ink-dark tracking-tight" data-id="element-2702" data-mp="3f3odmnrl34wb">
              Seven solutions
            </h2>
            <a href={demoCenterPath} className="inline-flex items-center gap-2 text-sm font-semibold text-navy hover:text-navy-light transition-colors duration-150 whitespace-nowrap" data-id="element-2703" data-mp="2nci7nqp6mcz4">
              Compare all demonstrations <ArrowRight size={14} aria-hidden="true" data-id="element-2704" data-mp="2werlapfqymgq" />
            </a>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-id="element-2705" data-mp="gndgoo993d6s">
            {solutions.map((solution, index) => <motion.li key={solution.id} initial={{
            opacity: 0,
            y: 12
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.25,
            delay: Math.min(index * 0.04, 0.24),
            ease: easeOutStrong
          }} className="flex" data-id="element-2706" data-mp="jn5h4gswv2z2" data-mpi={index}>
                <a href={solution.href} className="group flex w-full flex-col rounded-lg border border-line bg-white p-6 hover:border-navy/40 transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal" data-id="element-2707" data-mp="xkk99tajtouq">
                  <DomainIcon id={solution.id} data-id="element-2708" data-mp="eoaqisopca4e" />
                  <h3 className="mt-5 font-heading font-bold text-xl text-ink-dark" data-id="element-2709" data-mp="ep3nx31rtn9v">{solution.pageTitle}</h3>
                  <p className="mt-1 text-sm font-medium text-teal" data-id="element-2710" data-mp="3dboq8djh3325">{solution.offering}</p>
                  <p className="mt-3 text-ink-muted leading-relaxed" data-id="element-2711" data-mp="3ib1cox6fm847">{solution.summary}</p>
                  <p className="mt-4 text-sm text-ink-dark" data-id="element-2712" data-mp="3asusn7flo3sy">
                    <span className="font-semibold" data-id="element-2713" data-mp="35u0a86ljoh9d">Potential value: </span>
                    {solution.impact.benefits[0].title}
                  </p>
                  <span className="mt-auto pt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-navy group-hover:text-navy-light transition-colors duration-150" data-id="element-2714" data-mp="30x9tri5tvjnf">
                    Explore {solution.navLabel} <ArrowRight size={14} aria-hidden="true" data-id="element-2715" data-mp="2rwldegsyp77d" />
                  </span>
                </a>
              </motion.li>)}
          </ul>
        </div>
      </section>
    </>;
}